import { useState } from 'react'
import {
  confidenceBadgeClass,
  normalizeConfidenceLevel,
} from '../governance/governanceIntelligenceInteractionUx'
import {
  buildCitationPrimary,
  buildCitationSecondary,
  cleanAssistantContent,
  dedupeCitations,
  formatDate,
  formatExecutionAttribution,
  formatResponseStatusLabel,
  safeText,
} from './conversationHelpers'
import type { ConversationMessage } from './types'
import './defendsphere-ai.css'

type TranslateFn = (key: string, fallback?: string) => string

export function DefendSphereAiMessage({
  msg,
  t,
}: {
  msg: ConversationMessage
  t: TranslateFn
}) {
  const [showAllSources, setShowAllSources] = useState(false)
  const confidence = normalizeConfidenceLevel(msg.confidenceLevel)
  const diagnostics = (msg.diagnostics || {}) as Record<string, unknown>
  const executionMetadata =
    msg.executionMetadata ||
    (diagnostics.executionMetadata as ConversationMessage['executionMetadata']) ||
    null
  const attributionLine =
    msg.role === 'assistant' ? formatExecutionAttribution(executionMetadata, t) : null
  const statusLabel =
    msg.role === 'assistant'
      ? formatResponseStatusLabel(
          msg.responseStatus || (diagnostics.responseStatus as string) || null,
          Boolean(msg.insufficientContext),
          t,
        )
      : null
  const diagnosticsJson = msg.diagnostics
    ? JSON.stringify(
        {
          providerType: diagnostics.providerType,
          retrievalMode: diagnostics.retrievalMode,
          actualProviderKey: diagnostics.actualProviderKey,
          adapterType: diagnostics.adapterType,
          simulated: diagnostics.simulated,
          domainCounts: diagnostics.domainCounts,
          ragRunId: diagnostics.ragRunId,
          reasoningRunId: diagnostics.reasoningRunId,
          answerType: diagnostics.answerType || msg.answerType,
          displayMode: diagnostics.displayMode || msg.displayMode,
          answerComposerVersion: diagnostics.answerComposerVersion,
          answerQuality: diagnostics.answerQuality,
          narrativeLayer: diagnostics.narrativeLayer,
          executionMetadata,
          fallbackUsed: diagnostics.fallbackUsed,
          fallbackReason: diagnostics.fallbackReason,
          responseStatus: diagnostics.responseStatus || msg.responseStatus,
          requestId: executionMetadata?.requestId || null,
        },
        null,
        2,
      )
    : ''
  const cleanedContent = cleanAssistantContent(msg.content)
  const sourceSummary = msg.sourceSummary || []
  const renderedCitations = sourceSummary.length
    ? []
    : dedupeCitations([...(msg.standardsCitations || []), ...(msg.citations || [])])
  const visibleSourceSummary = showAllSources ? sourceSummary : sourceSummary.slice(0, 6)
  const isStandardEducationIntent =
    msg.answerType === 'STANDARD_EXPLANATION' ||
    msg.answerType === 'CONTROL_EXPLANATION' ||
    msg.detectedIntent === 'STANDARD_EXPLANATION' ||
    msg.detectedIntent === 'CONTROL_EXPLANATION' ||
    msg.detectedIntent === 'STANDARD_COMPARISON'
  const composerFindings = (msg.sections?.keyFindings || []).filter(Boolean)
  const riskFindings = (msg.topRisks || [])
    .map((risk) => safeText(risk.excerpt || `${risk.entityType || ''} ${risk.entityId || ''}`.trim()))
    .filter((text) => text && text.toLowerCase() !== cleanedContent.toLowerCase())
  const keyFindingsBullets = composerFindings.length ? composerFindings : riskFindings
  const showKeyFindings =
    msg.role === 'assistant' &&
    !isStandardEducationIntent &&
    (keyFindingsBullets.length > 0 || msg.evidenceSufficient != null)
  const isClarifyOnly = (text: string) =>
    /уточните\s+вопрос|clarify\s+(your\s+)?question|refine\s+your\s+question|для\s+более\s+глубокого/i.test(
      String(text || ''),
    )
  const sectionRecommendations = (msg.sections?.recommendations || [])
    .filter(Boolean)
    .filter((r) => !isClarifyOnly(String(r)))
  const topRecommendationsFiltered = (msg.topRecommendations || []).filter(
    (rec) => !isClarifyOnly(String(rec?.title || rec?.description || '')),
  )
  const showRecommendations =
    msg.role === 'assistant' &&
    (topRecommendationsFiltered.length > 0 || sectionRecommendations.length > 0) &&
    msg.answerType !== 'STANDARD_EXPLANATION'
  const prioritizedItems = msg.sections?.prioritizedItems || []
  const actionPlan = msg.sections?.actionPlan
  const managementSummary = msg.sections?.managementSummary
  const nonEmptyHorizons = (actionPlan?.horizons || []).filter(
    (horizon) => (horizon.actions || []).length > 0,
  )
  const showVcisoPlan =
    msg.role === 'assistant' &&
    (prioritizedItems.length > 0 || nonEmptyHorizons.length > 0)

  return (
    <article
      className={`ds-ai-message ${msg.role === 'user' ? 'ds-ai-message--user' : 'ds-ai-message--assistant'}`}
    >
      <header className="ds-ai-message__header">
        <span className="ds-ai-message__role">
          {msg.role === 'assistant'
            ? t('complianceCenterUi.nav.aiCiso', 'AI CISO')
            : msg.role}
        </span>
        {msg.role === 'assistant' && statusLabel ? (
          <span
            className={`ds-ai-badge ${
              msg.insufficientContext || msg.responseStatus === 'INSUFFICIENT_DATA'
                ? 'ds-ai-badge--warn'
                : msg.responseStatus === 'POLICY_BLOCKED' || msg.responseStatus === 'PROVIDER_ERROR'
                  ? 'ds-ai-badge--error'
                  : confidenceBadgeClass(confidence)
            }`}
          >
            {statusLabel}
          </span>
        ) : null}
        <span>{formatDate(msg.createdAt)}</span>
        {msg.role === 'assistant' && msg.confidenceLevel ? (
          <span className={`ds-ai-badge ${confidenceBadgeClass(confidence)}`}>{confidence}</span>
        ) : null}
        {msg.role === 'assistant' && msg.aiEnhancedExplanation ? (
          <span className="ds-ai-badge ds-ai-badge--enhanced">
            {t('complianceCenterUi.aiCiso.aiEnhancedExplanation', 'AI-enhanced explanation')}
          </span>
        ) : null}
      </header>

      {msg.role === 'assistant' && attributionLine ? (
        <p className="ds-ai-message__attribution" data-testid="ai-execution-attribution">
          {attributionLine}
        </p>
      ) : null}

      {msg.insufficientContext ? (
        <p className="ds-ai-warning" role="status">
          {t(
            'complianceCenterUi.convInsufficient',
            'Insufficient grounded context. Verify citations before acting on this response.',
          )}
        </p>
      ) : null}

      <p className="ds-ai-message__body">{cleanedContent || msg.content}</p>

      {showKeyFindings ? (
        <div className="ds-ai-message__section">
          <h4>{t('complianceCenterUi.aiCiso.keyFindings', 'Key findings')}</h4>
          {msg.evidenceSufficient != null ? (
            <p className="ds-ai-muted">
              {msg.evidenceSufficient
                ? t(
                    'complianceCenterUi.aiCiso.evidenceSufficient',
                    'Evidence appears sufficient for the analyzed scope.',
                  )
                : t(
                    'complianceCenterUi.aiCiso.evidenceInsufficient',
                    'Evidence gaps detected for the analyzed scope.',
                  )}
            </p>
          ) : null}
          {keyFindingsBullets.slice(0, 4).map((finding, idx) => (
            <p key={`${msg.id}-risk-${idx}`} className="ds-ai-muted">
              {finding}
            </p>
          ))}
        </div>
      ) : null}

      {showRecommendations && topRecommendationsFiltered.length ? (
        <div className="ds-ai-message__section">
          <h4>
            {t('complianceCenterUi.aiCiso.recommendations', 'Recommendations')}
            {msg.recommendationCount != null ? ` (${msg.recommendationCount})` : ''}
          </h4>
          {topRecommendationsFiltered.slice(0, 5).map((rec, idx) => (
            <div key={`${msg.id}-rec-${rec.id || idx}`} className="ds-ai-citation">
              <div className="ds-ai-citation__title">{rec.title || 'Recommendation'}</div>
              {rec.priority || rec.severity ? (
                <div className="ds-ai-muted">{[rec.priority, rec.severity].filter(Boolean).join(' · ')}</div>
              ) : null}
              {rec.description ? <p className="ds-ai-message__body">{rec.description}</p> : null}
            </div>
          ))}
        </div>
      ) : null}

      {showRecommendations && !topRecommendationsFiltered.length && sectionRecommendations.length ? (
        <div className="ds-ai-message__section">
          <h4>{t('complianceCenterUi.aiCiso.recommendations', 'Recommendations')}</h4>
          {sectionRecommendations.slice(0, 5).map((rec, idx) => (
            <p key={`${msg.id}-sec-rec-${idx}`} className="ds-ai-muted">
              {rec}
            </p>
          ))}
        </div>
      ) : null}

      {showVcisoPlan ? (
        <div className="ds-ai-message__section ds-ai-vciso">
          <h4>{t('complianceCenterUi.aiCiso.vcisoPriorities', 'Action priorities (advisory)')}</h4>
          <p className="ds-ai-muted">
            {t(
              'complianceCenterUi.aiCiso.vcisoAdvisoryNote',
              'Action priority bands are advisory and separate from official risk scores. Proposed actions are not approved or completed.',
            )}
          </p>
          {prioritizedItems.slice(0, 10).map((item, idx) => (
            <div key={`${msg.id}-prio-${item.rank || idx}`} className="ds-ai-citation">
              <div className="ds-ai-citation__title">
                #{item.rank || idx + 1} {item.title || 'Priority item'}
              </div>
              <div className="ds-ai-muted">
                {[item.actionPriority, item.confidence, item.urgencyNote]
                  .filter(Boolean)
                  .join(' · ')}
                {item.officialRiskLevel != null
                  ? ` · ${t('complianceCenterUi.aiCiso.officialRisk', 'Official risk')}: ${item.officialRiskLevel}`
                  : ''}
              </div>
              {(item.explanation?.missingFactors || []).length ? (
                <p className="ds-ai-muted">
                  {t('complianceCenterUi.aiCiso.missingFactors', 'Missing factors')}:{' '}
                  {(item.explanation?.missingFactors || []).slice(0, 4).join(', ')}
                </p>
              ) : null}
            </div>
          ))}

          {nonEmptyHorizons.length ? (
            <>
              <h4>{t('complianceCenterUi.aiCiso.plan306090', '30 / 60 / 90-day plan')}</h4>
              {nonEmptyHorizons.map((horizon) => (
                <div key={`${msg.id}-${horizon.id || horizon.label}`} className="ds-ai-plan-horizon">
                  <div className="ds-ai-citation__title">{horizon.label || horizon.id || 'Horizon'}</div>
                  {(horizon.actions || []).slice(0, 5).map((action, aIdx) => {
                    const verification = Array.isArray(action.verificationCriteria)
                      ? action.verificationCriteria.join('; ')
                      : action.verificationCriteria
                    return (
                      <div key={`${msg.id}-act-${aIdx}`} className="ds-ai-muted ds-ai-plan-action">
                        <strong>{action.title || action.proposedAction || 'Action'}</strong>
                        {action.actionPriority ? ` · ${action.actionPriority}` : ''}
                        {action.ownerUnknown
                          ? ` · ${t('complianceCenterUi.aiCiso.ownerUnknown', 'Owner unknown')}${
                              action.proposedResponsibilityRole
                                ? ` (${t('complianceCenterUi.aiCiso.proposedRole', 'proposed')}: ${action.proposedResponsibilityRole})`
                                : ''
                            }`
                          : ''}
                        {verification ? (
                          <div>
                            {t('complianceCenterUi.aiCiso.verification', 'Verification')}: {verification}
                          </div>
                        ) : null}
                        {action.expectedQualitativeBenefit ? (
                          <div>
                            {t('complianceCenterUi.aiCiso.expectedBenefit', 'Expected benefit')}:{' '}
                            {action.expectedQualitativeBenefit}
                            {!action.measuredOutcome
                              ? ` (${t('complianceCenterUi.aiCiso.notMeasured', 'not measured')})`
                              : ''}
                          </div>
                        ) : null}
                      </div>
                    )
                  })}
                </div>
              ))}
            </>
          ) : null}

          {managementSummary?.sections?.managementApprovals?.items?.length ? (
            <>
              <h4>
                {t(
                  'complianceCenterUi.aiCiso.decisionsNeedingApproval',
                  'Decisions requiring management approval',
                )}
              </h4>
              {(managementSummary.sections.managementApprovals.items || []).slice(0, 5).map((d, idx) => (
                <p key={`${msg.id}-appr-${idx}`} className="ds-ai-muted">
                  {String((d as { title?: string }).title || d)} ·{' '}
                  {t('complianceCenterUi.aiCiso.notApproved', 'not approved')}
                </p>
              ))}
            </>
          ) : null}
        </div>
      ) : null}

      {visibleSourceSummary.length ? (
        <div className="ds-ai-message__section">
          <h4>{t('complianceCenterUi.convCitations', 'Sources')}</h4>
          {visibleSourceSummary.map((s, idx) => (
            <div key={`${msg.id}-src-${idx}`} className="ds-ai-citation">
              <div className="ds-ai-citation__title">{s.primary}</div>
              <div className="ds-ai-muted">
                {[s.sourceType, (s as { temporalClass?: string }).temporalClass]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
              {s.secondary ? <p className="ds-ai-message__body">{s.secondary}</p> : null}
            </div>
          ))}
          {sourceSummary.length > 6 ? (
            <button
              type="button"
              className="ds-ai-linkish"
              onClick={() => setShowAllSources((v) => !v)}
            >
              {showAllSources
                ? t('complianceCenterUi.aiCiso.showFewerSources', 'Show fewer sources')
                : t('complianceCenterUi.aiCiso.showMoreSources', 'Show more sources')}
            </button>
          ) : null}
        </div>
      ) : renderedCitations.length ? (
        <div className="ds-ai-message__section">
          <h4>{t('complianceCenterUi.convCitations', 'Sources')}</h4>
          {renderedCitations.map((c, idx) => {
            const primary = buildCitationPrimary(c)
            const secondary = buildCitationSecondary(c, primary)
            return (
              <div key={`${msg.id}-c-${idx}`} className="ds-ai-citation">
                <div className="ds-ai-citation__title">{primary}</div>
                {c.sourceType ? <div className="ds-ai-muted">{c.sourceType}</div> : null}
                {secondary ? <p className="ds-ai-message__body">{secondary}</p> : null}
              </div>
            )
          })}
        </div>
      ) : null}

      {msg.role === 'assistant' && diagnosticsJson ? (
        <details className="ds-ai-message__section">
          <summary className="ds-ai-linkish">
            {t('complianceCenterUi.aiCiso.diagnostics', 'Technical diagnostics')}
          </summary>
          <pre className="ds-ai-pre">{diagnosticsJson}</pre>
        </details>
      ) : null}
    </article>
  )
}
